import { Button } from '@chiroup/components/ui/button';
import { ColumnDef, DataTable, DataTableColumnHeader } from '@chiroup/components/ui/data-table';
import { EhrNpiReportRow } from '@chiroup/core/types/EhrNpiReport.type';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { getEhrNpiReport } from '../../services/ehrNpiReport.service';
import Header from '../layout/Header';

const columns: ColumnDef<EhrNpiReportRow>[] = [
  {
    accessorKey: 'clinic_id',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Clinic ID" />,
    cell: ({ row }) => (
      <Link className="text-primary-600 hover:underline" to={`/clinics/${row.original.clinic_id}`}>
        {row.original.clinic_id}
      </Link>
    ),
  },
  {
    accessorKey: 'billDate',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Bill Date" />,
    cell: ({ row }) => row.original.billDate ?? '—',
  },
  {
    accessorKey: 'stripeSubscriptionId',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Stripe Subscription ID" />
    ),
    cell: ({ row }) => row.original.stripeSubscriptionId || '—',
  },
  {
    accessorKey: 'users_with_npi_count',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Users with NPI" />,
    meta: { align: 'right' },
  },
  {
    accessorKey: 'user_names',
    header: ({ column }) => <DataTableColumnHeader column={column} title="User Names" />,
    cell: ({ row }) => (
      <span className="whitespace-normal">{row.original.user_names || '—'}</span>
    ),
  },
];

const EhrNpiReport = () => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ['ehr-npi-report'],
    getEhrNpiReport,
    { refetchOnWindowFocus: false }
  );

  return (
    <>
      <Header title="EHR Clinic NPI Report" />
      <div className="p-6 space-y-4">
        <Link className="text-sm text-primary-600 hover:underline" to="/analytics">
          Back to Analytics
        </Link>
        <p className="text-sm text-gray-600 dark:text-dark-gray-400">
          All EHR clinics, ordered by the number of distinct users with a non-empty NPI in their
          clinic billing settings. Includes clinics with zero qualifying users.
        </p>
        {isError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            Unable to load the report. Please try refreshing.
          </p>
        )}
        <DataTable
          columns={columns}
          data={data?.rows ?? []}
          loading={isLoading}
          fetching={!!data && isFetching}
          pagination={{ pageSize: 25 }}
          toolbar={{
            search: { placeholder: 'Search clinics, subscriptions, or users...' },
            export: { csv: { filename: 'ehr-clinic-npi-report' } },
            rightSlot: (
              <Button variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
                {isFetching ? 'Refreshing...' : 'Refresh'}
              </Button>
            ),
          }}
        />
      </div>
    </>
  );
};

export default EhrNpiReport;
